import React, { useState, useEffect } from "react";
import { X, Loader } from "lucide-react";
import { useApi } from "../hooks/useApi";

const ENTITY_CONFIG = {
  deal: { label: "Deal", apiPath: "/deals/all", idKey: "dealID", nameKey: "dealName" },
  bu: { label: "Business Unit", apiPath: "/businessUnits/all", idKey: "businessUnitId", nameKey: "businessUnitName" },
  vendor: { label: "Vendor", apiPath: "/TPRMVendor/vendors?pageSize=100", idKey: "vendorId", nameKey: "vendorName", isVendor: true },
};

const SecretsScanEnrollEntityModal = ({
  isOpen, entityType, connections = [],
  existingEntityIds = [], onEnrolled, onClose,
}) => {
  const cfg = ENTITY_CONFIG[entityType] || ENTITY_CONFIG.deal;
  const apiCall = useApi();

  const [entities, setEntities] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [connectionId, setConnectionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedId("");
      setConnectionId(connections.length > 0 ? String(connections[0].id) : "");
      setError(null);
      fetchEntities();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEntities = async () => {
    setLoading(true);
    try {
      const res = await apiCall("GET", cfg.apiPath);
      const list = cfg.isVendor ? (res?.items || res || []) : (res || []);
      setEntities(list.filter(e => !existingEntityIds.includes(e[cfg.idKey])));
    } catch { setError(`Failed to load ${cfg.label.toLowerCase()}s`); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    const conn = connections.find(c => String(c.id) === connectionId);
    if (!conn) { setError("Select a Source Code Connection."); return; }

    setSaving(true); setError(null);
    try {
      onEnrolled?.(parseInt(selectedId, 10), conn.provider.toLowerCase(), conn.target, conn.baseUrl || null);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || `Failed to enroll ${cfg.label.toLowerCase()}`);
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border border-border w-full max-w-lg shadow-lg rounded-md bg-theme-secondary">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-content-primary">Add {cfg.label} Scan Target</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          {/* Entity selector */}
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-2">{cfg.label}</label>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
              className="w-full border border-border rounded-md px-3 py-2 bg-theme-primary text-content-primary" disabled={loading}>
              <option value="">-- Select a {cfg.label} --</option>
              {entities.map(e => (
                <option key={e[cfg.idKey]} value={e[cfg.idKey]}>{e[cfg.nameKey]}</option>
              ))}
            </select>
            {!loading && entities.length === 0 && (
              <p className="text-sm text-content-secondary mt-2">No {cfg.label.toLowerCase()}s available.</p>
            )}
          </div>

          {/* Connection selector — the target's repos and token come from the connection */}
          {selectedId && (
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-2">Source Code Connection</label>
              {connections.length > 0 ? (
                <select value={connectionId} onChange={e => setConnectionId(e.target.value)}
                  className="w-full border border-border rounded-md px-3 py-2 bg-theme-primary text-content-primary">
                  {connections.map(c => (
                    <option key={c.id} value={String(c.id)}>
                      {c.provider} / {c.target}{c.displayName ? ` — ${c.displayName}` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-red-600">
                  No Source Code Connections configured. Add one under Settings → Integrations first.
                </p>
              )}
              <p className="mt-1 text-xs text-content-secondary">
                The connection's repositories are scanned for secrets and findings are attributed to this {cfg.label.toLowerCase()}.
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-4">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-border text-content-secondary rounded-md hover:bg-theme-primary">Cancel</button>
            <button type="submit" disabled={!selectedId || !connectionId || saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? <><Loader className="h-4 w-4 animate-spin" />Adding...</> : `Add ${cfg.label}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SecretsScanEnrollEntityModal;
